import { FC } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import type { VariantChangeImpact } from '@src/types';

interface ConfirmVariantChangeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isSaving: boolean;
  impact: VariantChangeImpact | null;
  currencyCode?: string;
}

function formatPrice(cents: number, currencyCode: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(cents / 100);
}

const ConfirmVariantChangeDialog: FC<ConfirmVariantChangeDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  isSaving,
  impact,
  currencyCode = 'USD',
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
        Confirm Changes
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : impact ? (
          <>
            <Typography variant="h3" component="p" fontWeight={700} sx={{ mt: 1 }}>
              {impact.activeInventoryCount}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              active inventory items will be impacted by this change.
            </Typography>

            {impact.currentPrice !== impact.newPrice && (
              <>
                <Typography
                  variant="overline"
                  fontWeight={700}
                  sx={{ display: 'block', mb: 1 }}
                >
                  Price Change
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  {'This catalog item\'s price will change from '}
                  <strong>{formatPrice(impact.currentPrice, currencyCode)}</strong>
                  {' to '}
                  <strong>{formatPrice(impact.newPrice, currencyCode)}</strong>
                </Typography>
              </>
            )}

            <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
              Do you want to confirm and commit this change?
            </Typography>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Unable to load impact assessment. You can still proceed with saving.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isLoading || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          {isSaving ? 'Saving...' : 'Confirm & Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmVariantChangeDialog;
